import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay, format } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarEvent } from '@/services/calendarService';
import { Clock, AlertCircle } from 'lucide-react';

interface CalendarMonthViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
}

export default function CalendarMonthView({
  currentDate,
  events,
  selectedDate,
  onDateClick,
}: CalendarMonthViewProps) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = format(new Date(event.start), 'yyyy-MM-dd');
      return eventDate === dateStr;
    });
  };

  return (
    <div className="space-y-2">
      {/* Intestazione giorni settimana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>

      {/* Griglia calendario */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day) => {
          const dayEvents = getEventsForDate(day);
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isDayToday = isToday(day);
          const isSelected = selectedDate && isSameDay(day, selectedDate);

          return (
            <button
              key={day.toISOString()}
              onClick={() => onDateClick(day)}
              className={`
                relative min-h-[100px] p-2 border rounded-lg text-left
                transition-colors
                ${isCurrentMonth ? 'bg-white hover:bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'}
                ${isDayToday ? 'border-blue-500 border-2' : 'border-gray-200'}
                ${isSelected ? 'ring-2 ring-blue-500' : ''}
              `}
            >
              <div className={`text-sm font-medium mb-1 ${isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}`}>
                {format(day, 'd')}
              </div>
              
              {/* Eventi del giorno */}
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((event) => (
                  <div
                    key={event.id}
                    className={`text-xs px-1 py-0.5 rounded truncate flex items-center gap-1 ${
                      event.type === 'appointment' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-red-100 text-red-800'
                    }`}
                    title={event.title}
                  >
                    {event.type === 'appointment' ? (
                      <Clock className="h-3 w-3 shrink-0" />
                    ) : (
                      <AlertCircle className="h-3 w-3 shrink-0" />
                    )}
                    {event.type === 'appointment' && !event.allDay && format(new Date(event.start), 'HH:mm')}{' '}
                    <span className="truncate">
                      {event.title.length > 15 ? event.title.substring(0, 15) + '...' : event.title}
                    </span>
                  </div>
                ))}
                
                {dayEvents.length > 3 && (
                  <div className="text-xs text-gray-500 font-medium">
                    +{dayEvents.length - 3} altri
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


